import React from 'react';
import { viewFeed } from '@services/feedView';

export const RUNNING_STATUS = {
  INACTIVE: 'inactive',
  RUNNING: 'running',
  COMPLETED: 'completed',
  ERROR: 'error',
};

// Owns one /feed/view request at a time. The endpoint is a plain request/response read — no
// streaming, no job to poll — so the whole lifecycle is a status plus the last result.
export const useFeedView = () => {
  const [runningStatus, setRunningStatus] = React.useState(RUNNING_STATUS.INACTIVE);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [retryable, setRetryable] = React.useState(false);
  // What a failure was, beyond its message. Today that is only whether the server blamed the
  // `columns` field: a read whose floor-only retry ALSO failed is still evidence that this feed
  // refuses the column request, and it is the only evidence there is — that path returns no rows, so
  // it never reaches the result-filing effect that records a refusal on the success path.
  const [failure, setFailure] = React.useState(null);
  // The params of the request that produced `result`, so a Retry re-sends what was actually run
  // rather than whatever the form holds now.
  const lastParamsRef = React.useRef(null);
  // Monotonic id: a slow earlier response must not overwrite a newer one.
  const requestIdRef = React.useRef(0);
  // The in-flight request's controller, so a run can be cancelled rather than merely ignored.
  const controllerRef = React.useRef(null);
  const isMountedRef = React.useRef(true);
  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Don't leave a read running for a page nobody is looking at.
      controllerRef.current?.abort();
    };
  }, []);

  const run = React.useCallback(
    async (params) => {
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      lastParamsRef.current = params;

      // A resubmit supersedes the run in flight — stop it rather than paying for a response that
      // the monotonic guard below would only throw away.
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;

      setRunningStatus(RUNNING_STATUS.RUNNING);
      setError(null);
      setRetryable(false);
      setFailure(null);

      const response = await viewFeed({ ...params, signal: controller.signal });

      // Every exit returns, because a caller that navigated on the strength of this run has to know
      // whether the run arrived anywhere: the drill-down waits for this verdict before it
      // pushes a breadcrumb entry at all, so a read that lands nowhere leaves the path untouched.
      //
      // Null means one thing only: SOMETHING NEWER OWNS THE SCREEN. A newer run bumped the id, or
      // the page unmounted. Leave what's showing to whoever took it over. Note the ordering — this
      // guard runs before the abort check below, and a supersede aborts the old controller only
      // after bumping the id, so a superseded run always exits here and never reaches that branch.
      if (!isMountedRef.current || requestIdRef.current !== requestId) return null;
      controllerRef.current = null;

      // Cancelled on purpose, by this page's own Cancel button — the only way to reach this line.
      // Leave the previous result up and say nothing about the feed.
      //
      // The aborted response is RETURNED rather than swallowed as null, because the two cases the
      // caller has to tell apart are exactly these: after a supersede someone else will settle the
      // screen, and after a cancel NOBODY will. A caller that treated a cancel as "no verdict" left
      // the navigation it had already committed to standing over the old rows — a breadcrumb naming
      // the member it never reached, above the parent's table. There are no rows on this shape, so a
      // caller that unwinds on "came back with nothing" unwinds on a cancel for free.
      if (response.aborted) {
        setRunningStatus(result ? RUNNING_STATUS.COMPLETED : RUNNING_STATUS.INACTIVE);
        return response;
      }

      if (response.rows) {
        setResult(response);
        setRunningStatus(RUNNING_STATUS.COMPLETED);
        return response;
      }

      // Keep the prior table on screen on failure — losing it would punish an analyst for a 503.
      setError(response.error);
      setRetryable(!!response.retryable);
      setFailure({
        columnsImplicated: !!response.columnsImplicated,
        refusedColumns: response.refusedColumns || null,
      });
      setRunningStatus(RUNNING_STATUS.ERROR);
      return response;
    },
    [result]
  );

  // Put a result the caller already holds on screen, cancelling anything in flight. Two callers,
  // one body: stepping back up a drill-down path, where re-fetching a parent whose rows cannot have
  // changed would be pure waste, and the localhost fixture control.
  //
  // `forgetLastRun` is the only thing that ever separated them. A restored parent keeps the last
  // run's params so Try again still means something; a fixture has no params to retry — there is no
  // server on the other end of one — so it clears them rather than leaving a real feed's request
  // behind a button the fixture just put on screen.
  const present = React.useCallback((next, { forgetLastRun = false } = {}) => {
    if (!next) return;
    requestIdRef.current += 1;
    controllerRef.current?.abort();
    controllerRef.current = null;
    if (forgetLastRun) lastParamsRef.current = null;
    setResult(next);
    setError(null);
    setRetryable(false);
    setFailure(null);
    setRunningStatus(RUNNING_STATUS.COMPLETED);
  }, []);

  const restore = React.useCallback((previous) => present(previous), [present]);

  const cancel = React.useCallback(() => {
    controllerRef.current?.abort();
    controllerRef.current = null;
  }, []);

  const retry = React.useCallback(() => {
    if (!lastParamsRef.current) return null;
    return run(lastParamsRef.current);
  }, [run]);

  // Which feed a retry would actually re-read. `retry` re-sends the last params whatever they were,
  // and after a failed drill-down those are the MEMBER's — while the page has already rolled its own
  // idea of the displayed feed back to the parent. Without this the member's rows land under the
  // parent's name: the breadcrumb and the prompt panel describe the parent, the parent's hidden
  // columns apply, and the member's `available_columns` get filed as the parent's universe, so the
  // next parent read asks for a column that feed has never heard of. A getter rather than a value
  // because it reads a ref: it must answer for the run as it is at press time, not at last render.
  const lastRunFeedId = React.useCallback(() => lastParamsRef.current?.feedId ?? null, []);

  const reset = React.useCallback(() => {
    requestIdRef.current += 1;
    controllerRef.current?.abort();
    controllerRef.current = null;
    lastParamsRef.current = null;
    setResult(null);
    setError(null);
    setRetryable(false);
    setFailure(null);
    setRunningStatus(RUNNING_STATUS.INACTIVE);
  }, []);

  const isRunning = runningStatus === RUNNING_STATUS.RUNNING;

  return {
    runningStatus,
    isRunning,
    result,
    error,
    retryable,
    failure,
    // A table on screen while a newer run is loading is last run's answer, not this one's.
    isSuperseded: isRunning && !!result,
    run,
    present,
    restore,
    retry,
    lastRunFeedId,
    cancel,
    reset,
  };
};
